/**
 * `useThreadsStore` — Zustand slice that owns Conversation_Thread CRUD
 * and the active-thread cursor.
 *
 * Responsibilities (Requirements 4.1–4.3, 4.5, 4.7):
 *   - List thread summaries for the sidebar.
 *   - Hold the full active `ConversationThread` for the message list.
 *   - Create / rename / delete / clear / select threads.
 *   - Append / update messages and update the rolling summary.
 *   - On boot, restore the most-recently-updated thread, or create a
 *     default "Sohbet" thread so the chat surface is never empty
 *     (Requirement 4.2).
 *
 * The slice mirrors writes to the persistent store. The local copy of
 * `activeThread` is mutated in-place for cheap re-renders, and the
 * `summaries` array is refreshed after every write so the sidebar
 * `messageCount` stays in sync. The store's `appendMessage` /
 * `updateMessage` are atomic from the persistence side; the in-memory
 * copy is updated optimistically before the await resolves so the UI
 * sees the new message immediately.
 *
 * Thread selection is the only public way to swap `activeThread`. The
 * sidebar calls `selectThread(id)` and the slice loads the full row.
 *
 * Default-thread boot copy is "Sohbet" (Turkish for "Chat") because
 * the default conversation language is Turkish (Requirement 3.1).
 */

import { create } from 'zustand';

import type {
  ConversationThread,
  ConversationThreadSummary,
  Message,
} from '../domain/types';

import { getActiveConversationStore } from './storeContext';

/** Default first-thread title, in Turkish (Requirement 3.1). */
const DEFAULT_THREAD_TITLE = 'Sohbet';

/** Public slice surface. */
export interface ThreadsState {
  /** Sidebar projection — sorted by `updatedAt` DESC. */
  summaries: ConversationThreadSummary[];
  /** The currently displayed thread, or `null` between selections. */
  activeThread: ConversationThread | null;
  /** `true` once `hydrate()` has resolved. */
  hydrated: boolean;

  /**
   * Restore the most recent thread or create a default one
   * (Requirement 4.2).
   */
  hydrate(): Promise<void>;
  /** Load the full thread row for `id` and set it as active. */
  selectThread(id: string): Promise<void>;
  /** Create, persist, and select a new thread. */
  createThread(title: string): Promise<ConversationThread>;
  /** Rename a thread, refresh summaries, refresh active if relevant. */
  renameThread(id: string, title: string): Promise<void>;
  /**
   * Delete a thread. If it was active, clears `activeThread` (the
   * boot-style auto-select is intentionally NOT re-run — the caller
   * decides what to do next).
   */
  deleteThread(id: string): Promise<void>;
  /**
   * Empty the active thread's messages and rolling summary in place
   * (Requirement 4.7 — "clear this thread"). The thread row stays.
   */
  clearActiveThread(): Promise<void>;
  /** Append a message to the active thread and refresh summaries. */
  appendMessage(message: Message): Promise<void>;
  /** Patch a single message in the active thread. */
  updateMessage(messageId: string, patch: Partial<Message>): Promise<void>;
  /**
   * Update the rolling summary on the active thread. Marks every
   * message up to `summarizedThroughMessageId` as `summarized: true`.
   */
  setRollingSummary(
    summary: string,
    summarizedThroughMessageId: string,
  ): Promise<void>;
}

/**
 * Apply the same `setRollingSummary` semantics the persistence layer
 * applies (mark every message up to and including the cut-off as
 * `summarized: true`) to the local in-memory thread copy. Mirrors
 * `inMemory.ts` setRollingSummary so the local view stays in sync.
 */
function applyRollingSummaryLocally(
  thread: ConversationThread,
  summary: string,
  summarizedThroughMessageId: string,
): ConversationThread {
  const messages = thread.messages.map((m) => ({ ...m }));
  let crossed = false;
  for (const m of messages) {
    if (!crossed) {
      m.summarized = true;
      if (m.id === summarizedThroughMessageId) {
        crossed = true;
      }
    }
  }
  return {
    ...thread,
    rollingSummary: summary,
    summarizedThroughMessageId,
    messages,
  };
}

export const useThreadsStore = create<ThreadsState>((set, get) => ({
  summaries: [],
  activeThread: null,
  hydrated: false,

  async hydrate(): Promise<void> {
    const store = getActiveConversationStore();
    const summaries = await store.listThreads();
    if (summaries.length === 0) {
      // First boot or post-wipe — create a default thread so the chat
      // surface always has somewhere to land.
      const fresh = await store.createThread(DEFAULT_THREAD_TITLE);
      const refreshed = await store.listThreads();
      set({
        summaries: refreshed,
        activeThread: fresh,
        hydrated: true,
      });
      return;
    }
    // Restore most recent (summaries are sorted DESC by updatedAt).
    const head = summaries[0];
    if (head === undefined) {
      set({ summaries, hydrated: true });
      return;
    }
    const full = await store.getThread(head.id);
    set({
      summaries,
      activeThread: full,
      hydrated: true,
    });
  },

  async selectThread(id: string): Promise<void> {
    const store = getActiveConversationStore();
    const full = await store.getThread(id);
    set({ activeThread: full });
  },

  async createThread(title: string): Promise<ConversationThread> {
    const store = getActiveConversationStore();
    const created = await store.createThread(title);
    const summaries = await store.listThreads();
    set({ summaries, activeThread: created });
    return created;
  },

  async renameThread(id: string, title: string): Promise<void> {
    const store = getActiveConversationStore();
    await store.renameThread(id, title);
    const summaries = await store.listThreads();
    const active = get().activeThread;
    const nextActive: ConversationThread | null =
      active !== null && active.id === id
        ? { ...active, title }
        : active;
    set({ summaries, activeThread: nextActive });
  },

  async deleteThread(id: string): Promise<void> {
    const store = getActiveConversationStore();
    await store.deleteThread(id);
    const summaries = await store.listThreads();
    const active = get().activeThread;
    const nextActive = active !== null && active.id === id ? null : active;
    set({ summaries, activeThread: nextActive });
  },

  async clearActiveThread(): Promise<void> {
    const active = get().activeThread;
    if (active === null) return;
    const store = getActiveConversationStore();
    await store.clearThread(active.id);
    const summaries = await store.listThreads();
    set({
      summaries,
      activeThread: {
        ...active,
        messages: [],
        rollingSummary: null,
        summarizedThroughMessageId: null,
      },
    });
  },

  async appendMessage(message: Message): Promise<void> {
    const active = get().activeThread;
    if (active === null) {
      throw new Error('appendMessage: no active thread');
    }
    if (message.threadId !== active.id) {
      throw new Error(
        `appendMessage: message.threadId ${message.threadId} != active ${active.id}`,
      );
    }
    const store = getActiveConversationStore();
    await store.appendMessage(active.id, message);
    // Optimistic local update so the UI sees the bubble immediately
    // without waiting for a re-fetch (Requirement 11.2).
    const nextActive: ConversationThread = {
      ...active,
      messages: [...active.messages, { ...message }],
    };
    const summaries = await store.listThreads();
    set({ summaries, activeThread: nextActive });
  },

  async updateMessage(messageId, patch): Promise<void> {
    const active = get().activeThread;
    if (active === null) {
      throw new Error('updateMessage: no active thread');
    }
    const store = getActiveConversationStore();
    await store.updateMessage(active.id, messageId, patch);
    const idx = active.messages.findIndex((m) => m.id === messageId);
    if (idx === -1) {
      // Message not in local copy; fall back to a full reload.
      const full = await store.getThread(active.id);
      const summaries = await store.listThreads();
      set({ summaries, activeThread: full });
      return;
    }
    const messages = active.messages.slice();
    const existing = messages[idx];
    if (existing === undefined) {
      const full = await store.getThread(active.id);
      const summaries = await store.listThreads();
      set({ summaries, activeThread: full });
      return;
    }
    // id and threadId are immutable on the persistence side; mirror
    // that locally by overwriting them with the existing values.
    messages[idx] = {
      ...existing,
      ...patch,
      id: existing.id,
      threadId: existing.threadId,
    };
    const summaries = await store.listThreads();
    set({ summaries, activeThread: { ...active, messages } });
  },

  async setRollingSummary(summary, summarizedThroughMessageId): Promise<void> {
    const active = get().activeThread;
    if (active === null) {
      throw new Error('setRollingSummary: no active thread');
    }
    const store = getActiveConversationStore();
    await store.setRollingSummary(
      active.id,
      summary,
      summarizedThroughMessageId,
    );
    const next = applyRollingSummaryLocally(
      active,
      summary,
      summarizedThroughMessageId,
    );
    const summaries = await store.listThreads();
    set({ summaries, activeThread: next });
  },
}));
